<script lang="ts" setup>
  // Declare global window types for grecaptcha
  declare global {
    interface Window {
      grecaptcha?: {
        ready: (callback: () => void) => void;
        render: (container: string, parameters: Record<string, any>) => number;
        reset: (widgetId: number) => void;
        execute: (widgetId: number) => void;
      };
    }
  }

  const emit = defineEmits(['verify']);

  const { googleRecaptchaScript, googleRecaptchaSiteKey } = useRuntimeConfig().public;

  const captchaId = ref<number | undefined>(undefined);
  const isGoogleSignUp = ref(false);
  const isInitialized = ref(false);
  const scriptLoaded = ref(false);

  onMounted(() => {
    loadReCaptchaScript();
  });

  /**
   * Dynamically loads the Google ReCaptcha script
   * Only loads once when component is mounted
   */
  function loadReCaptchaScript() {
    // Check if script is already loaded
    if (window.grecaptcha || scriptLoaded.value) {
      initReCaptcha();
      return;
    }

    // Check if script tag already exists in DOM
    const existingScript = document.querySelector(`script[src="${googleRecaptchaScript}"]`);
    if (existingScript) {
      scriptLoaded.value = true;
      initReCaptcha();
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = googleRecaptchaScript;
    script.defer = true;
    script.onload = () => {
      scriptLoaded.value = true;
      initReCaptcha();
    };
    script.onerror = () => {
      console.error('Failed to load Google ReCaptcha script');
    };

    document.head.appendChild(script);
  }

  /**
   * Waits for the Google ReCaptcha script to load
   * Solves the race condition issue when loading the script with defer
   * @param {number} maxAttempts - maximum number of attempts
   * @param {number} delayMs - delay between attempts in milliseconds
   * @returns {Promise<boolean>} - true if script is loaded, false if timeout exceeded
   */
  async function waitForReCaptchaScript(maxAttempts = 50, delayMs = 100) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (window.grecaptcha) return true;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    return false;
  }

  async function initReCaptcha() {
    try {
      // Wait for script to load (maximum 5 seconds)
      const isLoaded = await waitForReCaptchaScript();

      if (!isLoaded) {
        console.error('Google ReCaptcha script failed to load within timeout period');
        return;
      }

      await render();
    } catch (error) {
      console.error('Failed to initialize ReCaptcha:', error);
    }
  }

  function reset() {
    if (window.grecaptcha && isInitialized.value && captchaId.value !== undefined) {
      try {
        window.grecaptcha.reset(captchaId.value);
      } catch (error) {
        console.error('Failed to reset ReCaptcha:', error);
      }
    }
  }

  function execute(isGoogle = false) {
    isGoogleSignUp.value = isGoogle;

    if (window.grecaptcha && isInitialized.value && captchaId.value !== undefined) {
      try {
        window.grecaptcha.execute(captchaId.value);
      } catch (error) {
        console.error('Failed to execute ReCaptcha:', error);
      }
    } else {
      console.warn('ReCaptcha is not initialized yet');
    }
  }

  function recaptchaCallback(response: string) {
    emit('verify', response, isGoogleSignUp.value);
    reset();
  }

  function errorCallback() {
    console.error('ReCaptcha error occurred');
  }

  async function render() {
    await nextTick();

    if (!window.grecaptcha) {
      console.error('window.grecaptcha is not available');
      return;
    }

    return new Promise<void>((resolve, reject) => {
      try {
        window.grecaptcha?.ready(() => {
          try {
            captchaId.value = window.grecaptcha?.render('g-recaptcha', {
              sitekey: googleRecaptchaSiteKey,
              size: 'invisible',
              callback: recaptchaCallback,
              'error-callback': errorCallback,
            });
            isInitialized.value = true;
            resolve();
          } catch (error) {
            console.error('Failed to render ReCaptcha:', error);
            reject(error);
          }
        });
      } catch (error) {
        console.error('Failed to initialize ReCaptcha ready callback:', error);
        reject(error);
      }
    });
  }

  defineExpose({
    execute,
    reset,
  });
</script>

<template>
  <ClientOnly>
    <div id="g-recaptcha" class="invisible" :data-sitekey="googleRecaptchaSiteKey" data-size="invisible" />
  </ClientOnly>
</template>
